import React, { useState } from 'react';
import { XIcon, GripVerticalIcon } from 'lucide-react';
interface ChartItem {
  id: string;
  title: string;
  value?: string;
  subtext?: string;
}
interface EditChartsModalProps {
  onClose: () => void;
  onSave: (chartOrder: string[], visibleCharts: Set<string>) => void;
  initialChartOrder: string[];
  initialVisibleCharts: Set<string>;
  chartData: Map<string, ChartItem>;
  renderChart: (chartId: string) => React.ReactNode;
}
export function EditChartsModal({
  onClose,
  onSave,
  initialChartOrder,
  initialVisibleCharts,
  chartData,
  renderChart
}: EditChartsModalProps) {
  const [chartOrder, setChartOrder] = useState<string[]>(initialChartOrder);
  const [visibleCharts, setVisibleCharts] = useState<Set<string>>(new Set(initialVisibleCharts));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newOrder = [...chartOrder];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dragOverIndex, 0, draggedItem);
      setChartOrder(newOrder);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleRemoveChart = (chartId: string) => {
    const newVisible = new Set(visibleCharts);
    newVisible.delete(chartId);
    setVisibleCharts(newVisible);
  };
  const handleSave = () => {
    onSave(chartOrder, visibleCharts);
    onClose();
  };
  // Filter to only show visible charts
  const visibleChartOrder = chartOrder.filter(id => visibleCharts.has(id));
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2589">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col" data-id="element-2590">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-2591">
          <div data-id="element-2592">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-2593">Edit charts</h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2594">
              Drag to reorder, click X to remove
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-2595">
            <XIcon size={20} className="text-gray-500" data-id="element-2596" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-2597">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2598">
            {visibleChartOrder.map((chartId, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            return <div key={chartId} draggable onDragStart={() => handleDragStart(index)} onDragOver={e => handleDragOver(e, index)} onDragEnd={handleDragEnd} className={`relative transition-all cursor-move ${isDragging ? 'opacity-50 scale-95' : isDragOver ? 'ring-2 ring-blue-500 rounded-lg' : ''}`} data-id="element-2599">
                  {/* Drag handle - positioned absolutely over the chart */}
                  <div className="absolute top-3 left-3 text-gray-400 bg-white rounded p-1 shadow-sm border border-gray-200 z-10" data-id="element-2600">
                    <GripVerticalIcon size={20} data-id="element-2601" />
                  </div>

                  {/* Remove button - positioned absolutely over the chart */}
                  <button onClick={() => handleRemoveChart(chartId)} className="absolute top-3 right-3 p-1 bg-white hover:bg-gray-100 rounded-full transition-colors z-10 shadow-sm border border-gray-200" data-id="element-2602">
                    <XIcon size={16} className="text-gray-500" data-id="element-2603" />
                  </button>

                  {/* Actual chart component - non-interactive */}
                  <div className="pointer-events-none" data-id="element-2604">
                    {renderChart(chartId)}
                  </div>
                </div>;
          })}
          </div>

          {/* Empty state */}
          {visibleChartOrder.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-2605">
              <div className="text-gray-400 mb-2" data-id="element-2606">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-2607">
                  <rect x="3" y="3" width="18" height="18" rx="2" data-id="element-2608" />
                  <path d="M3 9h18" data-id="element-2609" />
                  <path d="M9 21V9" data-id="element-2610" />
                </svg>
              </div>
              <p className="text-sm text-gray-500" data-id="element-2611">
                No charts to display. Add charts to get started.
              </p>
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-2612">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2613">
            Cancel
          </button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2614">
            Done
          </button>
        </div>
      </div>
    </div>;
}